import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { AuthModal } from '../auth/AuthModal';
import { t } from '../../lib/i18n';
import { Lang } from '../../types';
import toast from 'react-hot-toast';

export const Navbar: React.FC = () => {
  const { currentUser, logout, theme, toggleTheme, lang, setLang, unreadCount, mobileNavOpen, setMobileNavOpen } = useStore();
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
  const [missionsOpen, setMissionsOpen] = useState(false);
  const [mercsOpen, setMercsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const unread = unreadCount();

  const openAuth = (tab: 'login' | 'register') => { setAuthTab(tab); setAuthOpen(true); };

  const handleLogout = () => {
    logout();
    toast(t(lang, 'toast_logout'));
    navigate('/');
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      <nav className="navbar" role="navigation" aria-label="Main navigation">
        {/* Logo */}
        <Link to="/" className="nav-logo" onClick={() => setMobileNavOpen(false)}>
          <img
            src="/MMLogo.jpg"
            alt="Modern Mercenaries"
            className="nav-logo-img"
          />
          <span className="nav-logo-text">
            Modern
            <span>Mercenaries</span>
          </span>
        </Link>

        {/* Hamburger */}
        <button
          className="hamburger"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          aria-expanded={mobileNavOpen}
          aria-label="Toggle navigation"
        >
          {mobileNavOpen ? '✕' : '☰'}
        </button>

        {/* Nav items */}
        <div className={`nav-menu ${mobileNavOpen ? 'open' : ''}`}>
          {/* Missions dropdown */}
          <div
            className={`nav-item ${isActive('/missions') ? 'active' : ''}`}
            onMouseEnter={() => setMissionsOpen(true)}
            onMouseLeave={() => setMissionsOpen(false)}
          >
            <span>{t(lang, 'nav_missions')}</span>
            {missionsOpen && (
              <div className="dropdown">
                <Link to="/missions" onClick={() => { setMissionsOpen(false); setMobileNavOpen(false); }}>
                  {t(lang, 'nav_browse')}
                </Link>
                <Link to="/missions/create" onClick={() => {
                  setMissionsOpen(false); setMobileNavOpen(false);
                  if (!currentUser) { openAuth('login'); }
                }}>
                  {t(lang, 'nav_create')}
                </Link>
              </div>
            )}
            <div className="mobile-sub">
              <Link to="/missions" className="mobile-sub-link" onClick={() => setMobileNavOpen(false)}>{t(lang, 'nav_browse')}</Link>
              <Link to="/missions/create" className="mobile-sub-link" onClick={() => setMobileNavOpen(false)}>{t(lang, 'nav_create')}</Link>
            </div>
          </div>

          {/* Mercenaries dropdown */}
          <div
            className={`nav-item ${isActive('/mercenaries') ? 'active' : ''}`}
            onMouseEnter={() => setMercsOpen(true)}
            onMouseLeave={() => setMercsOpen(false)}
          >
            <span>{t(lang, 'nav_mercenaries')}</span>
            {mercsOpen && (
              <div className="dropdown">
                <Link to="/mercenaries" onClick={() => { setMercsOpen(false); setMobileNavOpen(false); }}>
                  {t(lang, 'nav_find')}
                </Link>
              </div>
            )}
            <div className="mobile-sub">
              <Link to="/mercenaries" className="mobile-sub-link" onClick={() => setMobileNavOpen(false)}>{t(lang, 'nav_find')}</Link>
            </div>
          </div>

          {currentUser && (
            <Link
              to="/profile"
              className={`nav-item ${isActive('/profile') ? 'active' : ''}`}
              onClick={() => setMobileNavOpen(false)}
            >
              {t(lang, 'nav_profile')}
            </Link>
          )}

          {currentUser && (
            <Link
              to="/chats"
              className={`nav-item ${isActive('/chats') ? 'active' : ''}`}
              onClick={() => setMobileNavOpen(false)}
            >
              {t(lang, 'nav_chats')}
              {unread > 0 && <span className="nav-badge">{unread}</span>}
            </Link>
          )}

          {!currentUser && (
            <button className="nav-item nav-btn" onClick={() => { openAuth('login'); setMobileNavOpen(false); }}>
              {t(lang, 'nav_signin')}
            </button>
          )}
          {currentUser && (
            <button className="nav-item nav-btn" onClick={() => { handleLogout(); setMobileNavOpen(false); }}>
              {t(lang, 'nav_signout')}
            </button>
          )}

          {/* Controls in mobile */}
          <div className="nav-controls-mobile">
            <select
              className="lang-select"
              value={lang}
              onChange={(e) => setLang(e.target.value as Lang)}
              aria-label="Language"
            >
              <option value="en">🇬🇧 EN</option>
              <option value="fr">🇫🇷 FR</option>
              <option value="es">🇪🇸 ES</option>
            </select>
            <button className="theme-btn" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>
        </div>

        {/* Desktop controls */}
        <div className="nav-controls-desktop">
          <select
            className="lang-select"
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
            aria-label="Language"
          >
            <option value="en">🇬🇧 EN</option>
            <option value="fr">🇫🇷 FR</option>
            <option value="es">🇪🇸 ES</option>
          </select>
          <button className="theme-btn" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </nav>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab={authTab} />
    </>
  );
};
